import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, CheckCircle, XCircle, GripVertical } from 'lucide-react';

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-2 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium ${type === 'success' ? 'bg-[#004D35]' : 'bg-red-600'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      {message}
    </div>
  );
};

const OptionRow = ({ option, onEdit, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(option.label);
  const [filterParam, setFilterParam] = useState(option.filter_param);
  const [filterValue, setFilterValue] = useState(option.filter_value);

  const save = () => {
    onEdit(option.id, { label, filter_param: filterParam, filter_value: filterValue });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 py-2 pl-4 flex-wrap">
        <input
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-[#004D35]"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Label shown to user"
        />
        <input
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[#004D35]"
          value={filterParam}
          onChange={e => setFilterParam(e.target.value)}
          placeholder="URL key (e.g. category)"
        />
        <input
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[#004D35]"
          value={filterValue}
          onChange={e => setFilterValue(e.target.value)}
          placeholder="URL value (e.g. cpa)"
        />
        <button onClick={save} className="px-3 py-1.5 bg-[#004D35] text-white text-sm rounded-lg font-medium hover:bg-[#003d2a]">Save</button>
        <button onClick={() => setEditing(false)} className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200">Cancel</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-2 pl-4 group hover:bg-gray-50 rounded-lg">
      <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
      <span className="text-sm flex-1">{option.label}</span>
      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-500">{option.filter_param}=</code>
      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-700">{option.filter_value}</code>
      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
        <button onClick={() => setEditing(true)} className="p-1 text-gray-400 hover:text-[#004D35] rounded">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(option.id)} className="p-1 text-gray-400 hover:text-red-500 rounded">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const AddOptionForm = ({ questionId, onAdd, onCancel }) => {
  const [label, setLabel] = useState('');
  const [filterParam, setFilterParam] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!label.trim() || !filterParam.trim() || !filterValue.trim()) return;
    onAdd(questionId, { label: label.trim(), filter_param: filterParam.trim(), filter_value: filterValue.trim() });
    setLabel('');
    setFilterParam('');
    setFilterValue('');
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 py-2 pl-4 mt-1 flex-wrap">
      <input
        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-[#004D35]"
        value={label}
        onChange={e => setLabel(e.target.value)}
        placeholder="Label (e.g. Just me)"
        autoFocus
        required
      />
      <input
        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[#004D35]"
        value={filterParam}
        onChange={e => setFilterParam(e.target.value)}
        placeholder="URL key (e.g. category)"
        required
      />
      <input
        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[#004D35]"
        value={filterValue}
        onChange={e => setFilterValue(e.target.value)}
        placeholder="URL value (e.g. cpa)"
        required
      />
      <button type="submit" className="px-3 py-1.5 bg-[#004D35] text-white text-sm rounded-lg font-medium hover:bg-[#003d2a]">Add</button>
      <button type="button" onClick={onCancel} className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200">Cancel</button>
    </form>
  );
};

const QuestionCard = ({ question, onUpdate, onDelete, onAddOption, onEditOption, onDeleteOption }) => {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [questionText, setQuestionText] = useState(question.question_text);
  const [addingOption, setAddingOption] = useState(false);

  const saveQuestion = () => {
    onUpdate(question.id, { question_text: questionText.trim() });
    setEditing(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-4 shadow-sm">
      <div className="flex items-center gap-3 px-5 py-4">
        <GripVertical className="w-5 h-5 text-gray-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex flex-col gap-2">
              <input
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#004D35]"
                value={questionText}
                onChange={e => setQuestionText(e.target.value)}
                placeholder="Question shown to users on homepage"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button onClick={saveQuestion} className="px-4 py-2 bg-[#004D35] text-white text-sm rounded-lg font-medium hover:bg-[#003d2a]">Save</button>
                <button onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="font-semibold text-gray-900 text-sm leading-tight">{question.question_text}</div>
              <div className="text-xs text-gray-400 mt-0.5">{(question.options || []).length} option{(question.options || []).length !== 1 ? 's' : ''} · display order {question.display_order}</div>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {!editing && (
            <button onClick={() => setEditing(true)} className="p-2 text-gray-400 hover:text-[#004D35] rounded-lg hover:bg-gray-100">
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onDelete(question.id)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-4">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Options</span>
              <span className="text-xs text-gray-400">filter_param → filter_value (appended to ?browse URL)</span>
            </div>
            {!addingOption && (
              <button
                onClick={() => setAddingOption(true)}
                className="flex items-center gap-1 text-xs text-[#004D35] font-semibold hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add option
              </button>
            )}
          </div>

          {(question.options || []).map(opt => (
            <OptionRow
              key={opt.id}
              option={opt}
              onEdit={onEditOption}
              onDelete={onDeleteOption}
            />
          ))}

          {addingOption && (
            <AddOptionForm
              questionId={question.id}
              onAdd={(qId, data) => { onAddOption(qId, data); setAddingOption(false); }}
              onCancel={() => setAddingOption(false)}
            />
          )}

          {(question.options || []).length === 0 && !addingOption && (
            <p className="text-xs text-gray-400 pl-4 py-2">No options yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

const AddQuestionForm = ({ onAdd, onCancel }) => {
  const [questionText, setQuestionText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!questionText.trim()) return;
    onAdd({ question_text: questionText.trim(), display_order: 999 });
  };

  return (
    <div className="bg-white border-2 border-dashed border-[#004D35] rounded-2xl p-5 mb-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">New Question</h3>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <input
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004D35]"
          value={questionText}
          onChange={e => setQuestionText(e.target.value)}
          placeholder="Question text shown on homepage (e.g. What type of advisor do you need?)"
          autoFocus
          required
        />
        <p className="text-xs text-gray-400 -mt-1">After creating, add options with their URL key/value pairs.</p>
        <div className="flex items-center gap-2">
          <button type="submit" className="px-4 py-2 bg-[#004D35] text-white text-sm rounded-lg font-semibold hover:bg-[#003d2a]">Create</button>
          <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200">Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default function AdminGuidedQuestions() {
  const { getAuthHeader } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/guided-questions', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setQuestions(data.questions || []);
      } else {
        showToast('Failed to load questions', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const handleAddQuestion = async (body) => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch('/api/guided-questions', {
        method: 'POST',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showToast('Question created');
        setShowAddForm(false);
        fetchQuestions();
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed to create question', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  const handleUpdateQuestion = async (id, body) => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/guided-questions/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showToast('Question updated');
        fetchQuestions();
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed to update question', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Delete this question and all its options?')) return;
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/guided-questions/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });
      if (res.ok) {
        showToast('Question deleted');
        fetchQuestions();
      } else {
        showToast('Failed to delete question', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  const handleAddOption = async (questionId, body) => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/guided-questions/${questionId}/options`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showToast('Option added');
        fetchQuestions();
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed to add option', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  const handleEditOption = async (optionId, body) => {
    try {
      const headers = await getAuthHeader();
      const questionId = questions.find(q => q.options?.some(o => o.id === optionId))?.id;
      if (!questionId) return;
      const res = await fetch(`/api/guided-questions/${questionId}/options/${optionId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showToast('Option updated');
        fetchQuestions();
      } else {
        const d = await res.json();
        showToast(d.error || 'Failed to update option', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Delete this option?')) return;
    try {
      const headers = await getAuthHeader();
      const questionId = questions.find(q => q.options?.some(o => o.id === optionId))?.id;
      if (!questionId) return;
      const res = await fetch(`/api/guided-questions/${questionId}/options/${optionId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });
      if (res.ok) {
        showToast('Option deleted');
        fetchQuestions();
      } else {
        showToast('Failed to delete option', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  return (
    <AdminLayout activeTab="questions">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>Guided Quiz Questions</h1>
            <p className="text-sm text-gray-500 mt-1">These questions appear on the homepage hero to help visitors find the right advisor. Each option maps to a URL filter param.</p>
          </div>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#004D35] text-white text-sm rounded-full font-semibold hover:bg-[#003d2a] transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Question
            </button>
          )}
        </div>

        {showAddForm && (
          <AddQuestionForm onAdd={handleAddQuestion} onCancel={() => setShowAddForm(false)} />
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white border border-gray-200 rounded-2xl h-20 animate-pulse" />
            ))}
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">❓</p>
            <p className="font-medium text-gray-600">No questions yet</p>
            <p className="text-sm mt-1">Add a question to power the homepage quiz.</p>
          </div>
        ) : (
          questions.map(q => (
            <QuestionCard
              key={q.id}
              question={q}
              onUpdate={handleUpdateQuestion}
              onDelete={handleDeleteQuestion}
              onAddOption={handleAddOption}
              onEditOption={handleEditOption}
              onDeleteOption={handleDeleteOption}
            />
          ))
        )}
      </div>
    </AdminLayout>
  );
}
